'use client';

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Row,
  Col,
  Space,
  Button,
  Input,
  Select,
  Modal,
  Form,
  Popconfirm,
  Tooltip,
} from 'antd';
import { toast } from 'react-toastify';
import {
  ShopOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DollarOutlined,
  FileTextOutlined,
  BankOutlined,
  PhoneOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useRouter } from 'next/navigation';
import { SupplierRecord, BankAccount } from '@/lib/mockSupplierStore';
import { useAuth } from '@/components/AuthContext';

const { Title, Text, Paragraph } = Typography;

export default function SuppliersPage() {
  const router = useRouter();
  const { user } = useAuth();
  const isManager = user.roles.includes('ADMIN') || user.roles.includes('PURCHASER');

  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('ALL');

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<SupplierRecord | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/suppliers');
      const json = await res.json();
      if (json.success) {
        setSuppliers(json.data);
      }
    } catch {
      toast.error('Gagal mengambil data supplier');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Open Create Modal
  const handleOpenCreate = () => {
    setEditingSupplier(null);
    form.resetFields();
    form.setFieldsValue({
      paymentTerms: 'NET 30',
      bankName: 'Bank Central Asia (BCA)',
    });
    setModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (record: SupplierRecord) => {
    setEditingSupplier(record);
    const primaryBank = record.bankAccounts?.find((b) => b.isPrimary) || record.bankAccounts?.[0];
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      phone: record.phone,
      email: record.email,
      address: record.address,
      paymentTerms: record.paymentTerms,
      bankName: primaryBank?.bankName || 'Bank Central Asia (BCA)',
      accountNumber: primaryBank?.accountNumber || '',
      accountHolderName: primaryBank?.accountHolderName || record.name,
    });
    setModalOpen(true);
  };

  // Submit Create or Edit Form
  const handleSubmitForm = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const bankAccounts: BankAccount[] = [
        {
          bankName: values.bankName,
          accountNumber: values.accountNumber,
          accountHolderName: values.accountHolderName || values.name,
          isPrimary: true,
        },
      ];

      const payload = {
        code: values.code,
        name: values.name,
        phone: values.phone,
        email: values.email,
        address: values.address,
        paymentTerms: values.paymentTerms,
        bankAccounts,
      };

      let res: Response;
      if (editingSupplier) {
        res = await fetch(`/api/suppliers/${editingSupplier.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch('/api/suppliers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Gagal menyimpan data supplier');
      }

      toast.success(json.message || 'Supplier berhasil disimpan');
      setModalOpen(false);
      fetchSuppliers();
    } catch (err: unknown) {
      if (err instanceof Error) {
        toast.error(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Supplier
  const handleDeleteSupplier = async (id: string) => {
    try {
      const res = await fetch(`/api/suppliers/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Gagal menghapus supplier');
      }
      toast.success(json.message || 'Supplier berhasil dihapus');
      fetchSuppliers();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Gagal menghapus supplier');
    }
  };

  // Filtering
  const filteredSuppliers = suppliers.filter((s) => {
    const q = searchQuery.toLowerCase().trim();
    const matchSearch =
      s.code.toLowerCase().includes(q) ||
      s.name.toLowerCase().includes(q) ||
      s.phone.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q) ||
      s.address.toLowerCase().includes(q);

    if (!matchSearch) return false;
    if (paymentFilter !== 'ALL' && s.paymentTerms !== paymentFilter) return false;

    return true;
  });

  const columns = [
    {
      title: 'Kode',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (c: string, r: SupplierRecord) => (
        <Tag
          color="purple"
          onClick={() => router.push(`/master/suppliers/${r.id}`)}
          style={{ cursor: 'pointer', fontWeight: 700, borderRadius: 6, fontSize: 12 }}
        >
          {c}
        </Tag>
      ),
    },
    {
      title: 'Nama Perusahaan Supplier',
      key: 'name',
      render: (_: unknown, r: SupplierRecord) => (
        <div>
          <div
            style={{ fontWeight: 700, color: '#0F172A', fontSize: 14, cursor: 'pointer' }}
            onClick={() => router.push(`/master/suppliers/${r.id}`)}
          >
            {r.name}
          </div>
          {r.address && (
            <div
              style={{
                color: '#64748B',
                fontSize: 12,
                marginTop: 2,
                maxWidth: 340,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={r.address}
            >
              {r.address}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Kontak',
      key: 'contact',
      width: 210,
      render: (_: unknown, r: SupplierRecord) => (
        <div style={{ fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#334155' }}>
            <PhoneOutlined style={{ color: '#6366F1' }} /> {r.phone}
          </div>
          {r.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748B', marginTop: 3 }}>
              <MailOutlined style={{ color: '#6366F1' }} /> {r.email}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Termin Bayar',
      dataIndex: 'paymentTerms',
      key: 'paymentTerms',
      width: 140,
      render: (t: string) => {
        let color = 'default';
        if (t === 'COD' || t === 'CBD') color = 'cyan';
        if (t === 'NET 14' || t === 'NET 30') color = 'purple';
        if (t === 'NET 60') color = 'gold';
        return (
          <Tag color={color} style={{ fontWeight: 600, borderRadius: 6, fontSize: 11 }}>
            {t}
          </Tag>
        );
      },
    },
    {
      title: 'Rekening Bank Utama',
      key: 'bank',
      render: (_: unknown, r: SupplierRecord) => {
        const bank = r.bankAccounts?.find((b) => b.isPrimary) || r.bankAccounts?.[0];
        if (!bank) return <span style={{ color: '#94A3B8', fontSize: 12 }}>Belum terdaftar</span>;
        return (
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 600, color: '#1E293B' }}>
              <BankOutlined style={{ color: '#6366F1', marginRight: 6 }} />
              {bank.bankName}
            </div>
            <div style={{ color: '#64748B', fontSize: 11 }}>
              {bank.accountNumber} a/n {bank.accountHolderName}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'success' : 'default'} style={{ borderRadius: 6, fontWeight: 600 }}>
          {status === 'ACTIVE' ? 'Aktif' : 'Nonaktif'}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 160,
      align: 'right' as const,
      render: (_: unknown, r: SupplierRecord) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => router.push(`/master/suppliers/${r.id}`)}
            style={{ borderRadius: 6, background: '#6366F1', fontSize: 12 }}
          >
            Detail
          </Button>
          {isManager && (
            <>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleOpenEdit(r)}
                style={{ borderRadius: 6 }}
              />
              <Popconfirm
                title="Hapus / Nonaktifkan Supplier"
                description={`Apakah Anda yakin ingin menghapus '${r.name}'? Jika ada transaksi PO, status akan diubah menjadi nonaktif.`}
                onConfirm={() => handleDeleteSupplier(r.id)}
                okText="Ya, Hapus"
                cancelText="Batal"
                okButtonProps={{ danger: true }}
              >
                <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: 40 }}>
      {/* Header Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Title level={3} style={{ margin: 0, color: '#0F172A', fontWeight: 800 }}>
              Modul Supplier
            </Title>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#6366F1',
                background: '#EEF2FF',
                border: '1px solid #C7D2FE',
                padding: '3px 10px',
                borderRadius: 20,
              }}
            >
              Master Vendor & Rekanan
            </span>
          </div>
          <Text type="secondary" style={{ fontSize: 13, marginTop: 4, display: 'block' }}>
            Manajemen profil rekanan vendor, termin pembayaran, rekening bank, serta riwayat transaksi dan katalog harga produk.
          </Text>
        </div>

        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchSuppliers} style={{ borderRadius: 10 }}>
            Segarkan
          </Button>
          {isManager && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreate}
              style={{
                borderRadius: 10,
                background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
                fontWeight: 600,
              }}
            >
              Tambah Supplier Baru
            </Button>
          )}
        </Space>
      </div>

      {/* KPI Pastel Stat Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #EEF2FF 100%)',
              padding: '18px 20px',
              borderRadius: 14,
              border: '1px solid #E0E7FF',
              boxShadow: '0 4px 16px -2px rgba(99, 102, 241, 0.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase' }}>
              Total Supplier Terdaftar
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#1E1B4B', marginTop: 4 }}>
              {suppliers.length}{' '}
              <span style={{ fontSize: 13, fontWeight: 600, color: '#4F46E5' }}>Rekanan</span>
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Mitra pengadaan aktif pabrik</div>
          </div>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)',
              padding: '18px 20px',
              borderRadius: 14,
              border: '1px solid #BBF7D0',
              boxShadow: '0 4px 16px -2px rgba(16, 185, 129, 0.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase' }}>
              Supplier Berstatus Aktif
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#064E3B', marginTop: 4 }}>
              {suppliers.filter((s) => s.status === 'ACTIVE').length}{' '}
              <span style={{ fontSize: 13, fontWeight: 600, color: '#15803D' }}>Supplier</span>
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Siap diterbitkan PO</div>
          </div>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F3FF 100%)',
              padding: '18px 20px',
              borderRadius: 14,
              border: '1px solid #DDD6FE',
              boxShadow: '0 4px 16px -2px rgba(124, 58, 237, 0.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6D28D9', textTransform: 'uppercase' }}>
              Termin Pembayaran Terbanyak
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#4C1D95', marginTop: 4 }}>
              NET 30
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Kredit jatuh tempo 30 hari</div>
          </div>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFFBEB 100%)',
              padding: '18px 20px',
              borderRadius: 14,
              border: '1px solid #FDE68A',
              boxShadow: '0 4px 16px -2px rgba(245, 158, 11, 0.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#B45309', textTransform: 'uppercase' }}>
              Total Transaksi Terhubung
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#78350F', marginTop: 4 }}>
              Rp 19.547.100
            </div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>Akumulasi PO terikat</div>
          </div>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card
        style={{
          borderRadius: 16,
          border: '1px solid #F1F5F9',
          boxShadow: '0 4px 20px -2px rgba(99, 102, 241, 0.04)',
        }}
        styles={{ body: { padding: '18px 20px' } }}
      >
        {/* Search & Filter Bar */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={14}>
            <Input
              placeholder="Cari berdasarkan Kode (SUP-...), Nama Supplier, Telepon, atau Alamat..."
              prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 10 }}
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              value={paymentFilter}
              onChange={setPaymentFilter}
              style={{ width: '100%', borderRadius: 10 }}
              options={[
                { value: 'ALL', label: 'Semua Termin Pembayaran' },
                { value: 'NET 30', label: 'NET 30 (Kredit 30 Hari)' },
                { value: 'NET 14', label: 'NET 14 (Kredit 14 Hari)' },
                { value: 'COD', label: 'COD (Cash on Delivery)' },
                { value: 'CBD', label: 'CBD (Cash Before Delivery)' },
              ]}
            />
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredSuppliers}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          size="middle"
        />
      </Card>

      {/* Create / Edit Supplier Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShopOutlined style={{ color: '#6366F1' }} />
            <span>{editingSupplier ? 'Edit Data Supplier' : 'Tambah Supplier Baru'}</span>
          </div>
        }
        open={modalOpen}
        onOk={handleSubmitForm}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        okText={editingSupplier ? 'Simpan Perubahan' : 'Tambahkan Supplier'}
        cancelText="Batal"
        width={680}
        style={{ top: 40 }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="code"
                label="Kode Supplier"
                tooltip="Opsional, sistem akan menghasilkan kode otomatis (e.g. SUP-004) jika dikosongkan."
              >
                <Input placeholder="Auto: SUP-..." style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="name"
                label="Nama Perusahaan Supplier"
                rules={[{ required: true, message: 'Nama supplier wajib diisi' }]}
              >
                <Input placeholder="Contoh: PT Aneka Tekstil Sejahtera" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Nomor Telepon"
                rules={[{ required: true, message: 'Nomor telepon wajib diisi' }]}
              >
                <Input placeholder="Contoh: +62-21-5551234" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="Alamat Email">
                <Input placeholder="Contoh: sales@supplier.co.id" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="paymentTerms"
                label="Termin Pembayaran"
                rules={[{ required: true, message: 'Termin pembayaran wajib dipilih' }]}
              >
                <Select
                  style={{ borderRadius: 8 }}
                  options={[
                    { value: 'NET 30', label: 'NET 30 (Kredit 30 Hari)' },
                    { value: 'NET 14', label: 'NET 14 (Kredit 14 Hari)' },
                    { value: 'NET 60', label: 'NET 60 (Kredit 60 Hari)' },
                    { value: 'COD', label: 'COD (Cash on Delivery)' },
                    { value: 'CBD', label: 'CBD (Cash Before Delivery)' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="address" label="Alamat Kantor / Pabrik">
                <Input placeholder="Alamat lengkap supplier..." style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{
              background: '#F8FAFC',
              padding: '14px 16px',
              borderRadius: 10,
              border: '1px solid #E2E8F0',
              marginTop: 10,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 10, textTransform: 'uppercase' }}>
              <BankOutlined style={{ color: '#6366F1', marginRight: 6 }} />
              Rekening Bank Utama Supplier
            </div>

            <Row gutter={12}>
              <Col span={8}>
                <Form.Item
                  name="bankName"
                  label="Nama Bank"
                  rules={[{ required: true, message: 'Nama bank wajib diisi' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    options={[
                      { value: 'Bank Central Asia (BCA)', label: 'BCA' },
                      { value: 'Bank Mandiri', label: 'Mandiri' },
                      { value: 'Bank Rakyat Indonesia (BRI)', label: 'BRI' },
                      { value: 'Bank Negara Indonesia (BNI)', label: 'BNI' },
                      { value: 'Bank Danamon', label: 'Danamon' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="accountNumber"
                  label="Nomor Rekening"
                  rules={[{ required: true, message: 'Nomor rekening wajib diisi' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Nomor rekening..." />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="accountHolderName"
                  label="Atas Nama"
                  rules={[{ required: true, message: 'Atas nama wajib diisi' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Nama pemilik..." />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
